import { useEffect, useMemo, useState } from 'react'
import { BottomSheet } from './BottomSheet.jsx'

// Calendario gregoriano a griglia, portato da gunsi-tally (versione
// semplificata: niente modalità Lil, niente popover desktop).
//
// Props: open, value ('YYYY-MM-DD'), onChange(iso), onClose, locale,
//        title, todayLabel

function pad(n) { return String(n).padStart(2, '0') }

function isoToParts(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  return { year: y, month: m, day: d }
}
function partsToIso({ year, month, day }) {
  return `${year}-${pad(month)}-${pad(day)}`
}
function isoToday() {
  const d = new Date()
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function shiftMonth(year, month, delta) {
  let m = month + delta
  let y = year
  while (m < 1)  { m += 12; y -= 1 }
  while (m > 12) { m -= 12; y += 1 }
  return { year: y, month: m }
}

function monthCells(year, month) {
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7 // Mon=0
  const lastDay = new Date(year, month, 0).getDate()
  const cells = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= lastDay; d++) cells.push(d)
  while (cells.length % 7) cells.push(null)
  return cells
}

function monthLabel(year, month, locale) {
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' })
    .format(new Date(year, month - 1, 1))
}

function weekdayHeaders(locale) {
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  const fmt = new Intl.DateTimeFormat(lang, { weekday: 'narrow' })
  // Settimana di riferimento che inizia di lunedì (5 Jan 1970)
  return [0, 1, 2, 3, 4, 5, 6].map(i => fmt.format(new Date(1970, 0, 5 + i)).toUpperCase())
}

export function DatePicker({ open, value, onChange, onClose, locale, title, todayLabel }) {
  const init = isoToParts(value || isoToday())
  const [view, setView] = useState({ year: init.year, month: init.month })
  const [yearView, setYearView] = useState(false)
  const [yearBase, setYearBase] = useState(init.year - 6)

  useEffect(() => {
    if (!open) return
    const parts = isoToParts(value || isoToday())
    setView({ year: parts.year, month: parts.month })
    setYearView(false)
  }, [open, value])

  const cells = useMemo(() => monthCells(view.year, view.month), [view])
  const weekdays = useMemo(() => weekdayHeaders(locale), [locale])
  const today = isoToday()
  const todayYear = Number(today.split('-')[0])

  function pick(day) {
    if (!day) return
    onChange(partsToIso({ year: view.year, month: view.month, day }))
    onClose()
  }

  return (
    <BottomSheet open={open} onClose={onClose} className="dp-sheet">
      <div className="dp-panel">
      <div className="dp-header">
        <span className="dp-title">{title}</span>
        <button type="button" className="dp-reset" onClick={() => { onChange(isoToday()); onClose() }}>
          {todayLabel}
        </button>
        <button type="button" className="dp-close" onClick={onClose} aria-label="close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="dp-month-nav">
        <button
          type="button"
          className="dp-arrow"
          onClick={() => yearView ? setYearBase(b => b - 12) : setView(shiftMonth(view.year, view.month, -1))}
          aria-label="Previous"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          type="button"
          className="dp-month-label dp-month-label-btn"
          onClick={() => { if (yearView) { setYearView(false) } else { setYearBase(view.year - 6); setYearView(true) } }}
        >
          {yearView ? `${yearBase}–${yearBase + 11}` : monthLabel(view.year, view.month, locale)}
        </button>
        <button
          type="button"
          className="dp-arrow"
          onClick={() => yearView ? setYearBase(b => b + 12) : setView(shiftMonth(view.year, view.month, +1))}
          aria-label="Next"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {yearView ? (
        <div className="dp-year-grid">
          {Array.from({ length: 12 }, (_, i) => yearBase + i).map(y => (
            <button
              key={y}
              type="button"
              className={`dp-year${y === view.year ? ' selected' : ''}${y === todayYear ? ' today' : ''}`}
              onClick={() => { setView(v => ({ ...v, year: y })); setYearView(false) }}
            >{y}</button>
          ))}
        </div>
      ) : (
        <>
          <div className="dp-weekdays">
            {weekdays.map((w, i) => <span key={i} className="dp-wkd">{w}</span>)}
          </div>
          <div className="dp-grid">
            {cells.map((d, i) => {
              if (d == null) return <span key={i} className="dp-cell empty" />
              const iso = partsToIso({ year: view.year, month: view.month, day: d })
              const selected = value === iso
              const isToday  = today === iso
              return (
                <button
                  key={i}
                  type="button"
                  className={`dp-cell${selected ? ' selected' : ''}${isToday ? ' today' : ''}`}
                  onClick={() => pick(d)}
                >{d}</button>
              )
            })}
          </div>
        </>
      )}
      </div>
    </BottomSheet>
  )
}
